import styled, { keyframes } from 'styled-components';
import { radius } from '@libertify/ui';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { Button, Flex, Typography } from 'antd';
import { PauseOutlined } from '@ant-design/icons';

import 'video.js/dist/video-js.css';
import '../styles/index.css';
import '../styles/app.css';
import '../styles/app.scss';
import '../styles/theme.css';

// Components
import VideoPlayer from './VideoPlayer';
import { PdfViewer } from './PdfViewer';
import MobileOverlay from './MobileOverlay';
import { MobileScreenSwap } from './MobileScreenSwap';
import { CTAs } from './CTAs';
import { TabWrapper } from './TabWrapper';
import { Desktop, Mobile } from './ResponsiveContainers';
import { Logo } from './Logo';
import { StyledTabs } from './StyledTabs';
import { ChatBot } from './Chat/ChatBot';
import { PromptForm } from './Chat/PromptForm';
import { SuggestionsOverlay } from './Chat/SuggestionsOverlay';
import { LanguageSelector } from './LanguageSelector';
import { AppProviders } from './AppProviders';
import VideoWrapper from './VideoWrapper';
import { DocumentChatPanel } from './DocumentChatPanel';
import { SparkleIcon } from './icons';

// Lazy-loaded In Doc layout — avoids pulling CircularVideoWrapper and its
// dependencies into the main bundle for non-InDoc projects.
const LazyInDocApp = React.lazy(() =>
  import('./InDocApp').then((mod) => ({ default: mod.InDocApp })),
);

// Hooks
import { useResponsiveAppState } from '../hooks/useResponsiveAppState';
import { useVideoPlayerSetup } from '../hooks/useVideoPlayerSetup';
import { useMobilePlayControls } from '../hooks/useMobilePlayControls';
import { useChatDragResize } from '../hooks/useChatDragResize';
import { useChatSubmitLabel } from '../hooks/useChatSubmitLabel';
import { useAntTheme } from '../hooks/useAntTheme';
import { useAnalytics } from '../hooks/useAnalytics';
import { getSessionId } from './Chat/utils';

// Helpers
import {
  hasChat,
  hasDocumentAnimation,
  isPromptProject,
  shouldShowLanguageSelector,
  videoOnly,
} from '../helpers';
import { getUrlAccessToken } from '../utils/auth';

// Types & Constants
import { MULTIPLE_DOCS_DISPLAY } from '../constants';
import {
  Layout,
  MediaProjectInstanceResponse,
  MediaAnalyticsEvents,
  LeftPanelProps,
  RightPanelProps,
  ResponsiveAppProps,
  ProjectDocument,
} from '../types';
import { StyledApp, StyledPanelResizeHandle } from './styles';
import { AppContext } from '../contexts/AppContext';

const { ChatOpenEvent, ChatCloseEvent } = MediaAnalyticsEvents;

// ─────────────────────────────────────────────────────────────
// Shared components
// ─────────────────────────────────────────────────────────────

const PoweredBy = React.memo(function PoweredBy({
  layout,
  style,
}: {
  layout: Layout;
  style?: React.CSSProperties;
}) {
  const { t } = useContext(AppContext);
  const { trackEvent } = useAnalytics();
  if (layout !== 'video') return null;

  return (
    <div className="powered-by" style={style}>
      <span>{t('poweredBy')}</span>{' '}
      <a
        href="https://www.libertify.com"
        target="_blank"
        rel="noreferrer"
        onClick={() =>
          trackEvent(MediaAnalyticsEvents.BrandingLinkClick, {
            link_text: t('libertify'),
            link_location: 'footer',
          })
        }
      >
        {t('libertify')}
      </a>
    </div>
  );
});

const AskQuestionsIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.57741 5.76368C9.03709 4.87064 9.26694 4.42412 9.57572 4.27944C9.84455 4.15348 10.1554 4.15348 10.4243 4.27944C10.7331 4.42412 10.9629 4.87064 11.4226 5.76368L12.6143 8.07877C12.6939 8.23349 12.7337 8.31085 12.7853 8.37917C12.831 8.43981 12.8835 8.49505 12.9417 8.54381C13.0073 8.59874 13.0825 8.64245 13.233 8.72989L15.6194 10.1166C16.4013 10.571 16.7922 10.7982 16.9237 11.0943C17.0383 11.3526 17.0383 11.6474 16.9237 11.9057C16.7922 12.2018 16.4013 12.429 15.6194 12.8834L13.233 14.2701C13.0825 14.3575 13.0073 14.4013 12.9417 14.4562C12.8835 14.505 12.831 14.5602 12.7853 14.6208C12.7337 14.6891 12.6939 14.7665 12.6143 14.9212L11.4226 17.2363C10.9629 18.1294 10.7331 18.5759 10.4243 18.7206C10.1554 18.8465 9.84455 18.8465 9.57572 18.7206C9.26694 18.5759 9.03709 18.1294 8.5774 17.2363L7.38572 14.9212C7.30608 14.7665 7.26625 14.6891 7.21474 14.6208C7.16902 14.5602 7.11653 14.505 7.0583 14.4562C6.9927 14.4013 6.91747 14.3575 6.76701 14.2701L4.38063 12.8834C3.59872 12.429 3.20777 12.2018 3.07632 11.9057C2.96166 11.6474 2.96166 11.3526 3.07632 11.0943C3.20777 10.7982 3.59872 10.571 4.38063 10.1166L6.76701 8.72989C6.91747 8.64245 6.9927 8.59874 7.0583 8.54381C7.11653 8.49505 7.16902 8.43981 7.21474 8.37917C7.26625 8.31085 7.30608 8.23349 7.38572 8.07877L8.57741 5.76368Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17.4595 19.4062C17.2058 19.089 17.0789 18.9304 17.0314 18.7475C16.9895 18.5866 16.9895 18.4134 17.0314 18.2525C17.0789 18.0696 17.2058 17.911 17.4595 17.5938L18.2751 16.5744C18.5288 16.2572 18.6557 16.0986 18.802 16.0392C18.9307 15.9869 19.0693 15.9869 19.198 16.0392C19.3443 16.0986 19.4712 16.2572 19.7249 16.5744L20.5405 17.5938C20.7942 17.911 20.9211 18.0696 20.9686 18.2525C21.0105 18.4134 21.0105 18.5866 20.9686 18.7475C20.9211 18.9304 20.7942 19.089 20.5405 19.4062L19.7249 20.4256C19.4712 20.7428 19.3443 20.9014 19.198 20.9608C19.0693 21.0131 18.9307 21.0131 18.802 20.9608C18.6557 20.9014 18.5288 20.7428 18.2751 20.4256L17.4595 19.4062Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.2298 4.36247C18.1029 4.23559 18.0394 4.17215 18.0157 4.099C17.9948 4.03466 17.9948 3.96534 18.0157 3.901C18.0394 3.82785 18.1029 3.76441 18.2298 3.63753L18.6375 3.22976C18.7644 3.10289 18.8278 3.03945 18.901 3.01568C18.9653 2.99477 19.0347 2.99477 19.099 3.01568C19.1722 3.03945 19.2356 3.10289 19.3625 3.22976L19.7702 3.63753C19.8971 3.76441 19.9605 3.82785 19.9843 3.901C20.0052 3.96534 20.0052 4.03466 19.9843 4.099C19.9605 4.17215 19.8971 4.23559 19.7702 4.36247L19.3625 4.77024C19.2356 4.89711 19.1722 4.96055 19.099 4.98432C19.0347 5.00523 18.9653 5.00523 18.901 4.98432C18.8278 4.96055 18.7644 4.89711 18.6375 4.77024L18.2298 4.36247Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─────────────────────────────────────────────────────────────
// Modern layout — chat overlays the video, video shrinks (newMode)
// ─────────────────────────────────────────────────────────────

interface ModernLeftPanelProps extends LeftPanelProps {
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  documentIds: string[];
  organizationId: string | undefined;
  handleDisplaySource: (source: import('../types').MessageOrigin) => void;
  cleanHighlightAndCuepoints: () => void;
  firstQuery: string | undefined;
  nextQuery: string | undefined;
  suggestions: string[] | undefined;
  setSuggestions: (s: string[] | undefined) => void;
  openChat: boolean;
  // Prompt projects render the chat in the right panel and keep the video
  // visible — skip the left-panel overlay rendering and chat-driven layout.
  isPromptProject: boolean;
}

const ModernLeftPanel: React.FC<ModernLeftPanelProps> = React.memo(
  function ModernLeftPanel({
    videoOnly: isVideoOnly,
    publication,
    videoJsOptions,
    cuepoints,
    highlights,
    sections,
    callToActions,
    onPlayerReady,
    showLogo,
    locale,
    isVideoLoaded,
    isCreatorMode,
    videoOverride,
    chatOpen,
    setChatOpen,
    mainDocument,
    documentIds,
    organizationId,
    handleDisplaySource,
    cleanHighlightAndCuepoints,
    firstQuery,
    nextQuery,
    suggestions,
    setSuggestions,
    openChat,
    isPromptProject: isPromptProjectFlag,
  }: ModernLeftPanelProps) {
    const { t } = useContext(AppContext);
    const searchParams =
      typeof globalThis.window === 'undefined'
        ? new URLSearchParams()
        : new URLSearchParams(globalThis.location.search);

    return (
      <div className="left-panel">
        <Logo
          showLogo={showLogo}
          projectId={publication?.project_id}
          customUrl={publication?.assets?.Logo?.path}
        />
        <div className="media-wrapper">
          {videoOverride ?? (
            <VideoWrapper isVideoLoaded={isVideoLoaded}>
              <VideoPlayer
                isLoading={!isVideoLoaded}
                isCreatorMode={isCreatorMode}
                options={videoJsOptions}
                onReady={onPlayerReady}
                cuepoints={cuepoints}
                highlights={highlights}
                sections={sections}
                callToActions={callToActions}
                publication={publication}
                locale={locale}
              />
            </VideoWrapper>
          )}

          {hasChat(publication) && chatOpen && !isPromptProjectFlag && (
            <div className="newmode-chat-overlay">
              <div className="chat-section chat-section--open">
                <div className="chat-section__header">
                  <div className="chat-section__header-left">
                    <SparkleIcon />
                    <span className="chat-section__header-title">
                      {t('askAboutDocument')}
                    </span>
                  </div>
                  <button
                    className="chat-section__close-btn"
                    onClick={() => setChatOpen(false)}
                    type="button"
                    aria-label="Close chat"
                  >
                    &#x2715;
                  </button>
                </div>
                <div className="chat-section__body">
                  <ChatBot
                    publication={publication}
                    open={openChat || chatOpen}
                    mainDocumentId={mainDocument?.id}
                    publicationDocuments={publication?.documents}
                    firstQuery={firstQuery}
                    nextQuery={nextQuery}
                    documentIds={documentIds}
                    organizationId={organizationId ?? ''}
                    showSources={handleDisplaySource}
                    onBeforeQuery={cleanHighlightAndCuepoints}
                    suggestions={suggestions}
                    setSuggestions={setSuggestions}
                    previewAccessToken={getUrlAccessToken(searchParams)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <PoweredBy
          layout={chatOpen && !isPromptProjectFlag ? 'chat' : 'video'}
          style={isVideoOnly ? { left: 'auto' } : {}}
        />
      </div>
    );
  },
);

type ModernRightPanelProps = RightPanelProps & {
  // Prompt projects render the chat inline in the right panel (legacy
  // DocumentChatPanel behavior) instead of overlaying on the left.
  isPromptProject: boolean;
};

const ModernRightPanel: React.FC<ModernRightPanelProps> = React.memo(
  function ModernRightPanel(props: ModernRightPanelProps) {
    const {
      showTitle,
      publication,
      mainDocument,
      highlights,
      cuepoints,
      pdfViewerRef,
      creatorDocumentFooter,
      isCreatorMode,
      chatOpen,
      setChatOpen,
      chatHeight,
      onDragStart,
      cardRef,
      firstQuery,
      nextQuery,
      openChat,
      suggestions,
      setSuggestions,
      documentIds,
      handleDisplaySource,
      cleanHighlightAndCuepoints,
      organizationId,
      isPromptProject: isPromptProjectFlag,
    } = props;

    return (
      <div className="right-panel">
        <Flex>
          {showTitle && (
            <div className="page-title">
              <Typography.Title level={1} style={{ margin: 0, fontSize: 26 }}>
                {publication.title}
              </Typography.Title>
            </div>
          )}
        </Flex>
        <div className="right-panel-content">
          <DocumentChatPanel
            publication={publication}
            mainDocument={mainDocument}
            highlights={highlights}
            cuepoints={cuepoints}
            pdfViewerRef={pdfViewerRef}
            organizationId={organizationId}
            chatOpen={chatOpen}
            setChatOpen={setChatOpen}
            chatHeight={chatHeight}
            onDragStart={onDragStart}
            firstQuery={firstQuery}
            nextQuery={nextQuery}
            openChat={openChat}
            suggestions={suggestions}
            setSuggestions={setSuggestions}
            documentIds={documentIds}
            handleDisplaySource={handleDisplaySource}
            cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
            isCreatorMode={isCreatorMode}
            afterDocument={creatorDocumentFooter}
            containerRef={cardRef as React.RefObject<HTMLDivElement>}
            containerStyle={{
              width: '100%',
              height: '100%',
              borderRadius: radius.box,
            }}
            chatInOverlay={!isPromptProjectFlag}
          />
        </div>
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────
// Legacy layout — chat in left panel (layout switching)
// ─────────────────────────────────────────────────────────────

interface LegacyLeftPanelProps extends LeftPanelProps {
  layout: Layout;
  setLayout: (layout: Layout) => void;
  mainDocument: import('../types').ProjectDocument | undefined;
  documentIds: string[];
  organizationId: string | undefined;
  handleDisplaySource: (source: import('../types').MessageOrigin) => void;
  cleanHighlightAndCuepoints: () => void;
  firstQuery: string | undefined;
  nextQuery: string | undefined;
  suggestions: string[] | undefined;
  setSuggestions: (s: string[] | undefined) => void;
  openChat: boolean;
}

const LegacyLeftPanel: React.FC<LegacyLeftPanelProps> = React.memo(
  function LegacyLeftPanel({
    layout,
    setLayout,
    videoOnly: isVideoOnly,
    publication,
    videoJsOptions,
    cuepoints,
    highlights,
    sections,
    callToActions,
    onPlayerReady,
    showSideCtas,
    showLogo,
    locale,
    isVideoLoaded,
    isCreatorMode,
    mainDocument,
    documentIds,
    organizationId,
    handleDisplaySource,
    cleanHighlightAndCuepoints,
    firstQuery,
    nextQuery,
    suggestions,
    setSuggestions,
    openChat,
  }: LegacyLeftPanelProps) {
    const handleVideoClick = useCallback(() => {
      if (layout === 'chat') setLayout('video');
    }, [layout, setLayout]);
    const searchParams =
      typeof globalThis.window === 'undefined'
        ? new URLSearchParams()
        : new URLSearchParams(globalThis.location.search);

    return (
      <div className="left-panel">
        <Logo
          showLogo={showLogo}
          projectId={publication?.project_id}
          customUrl={publication?.assets?.Logo?.path}
        />
        <CTAs
          showSideCtas={showSideCtas}
          callToActions={callToActions}
          publication={publication}
          locale={locale}
        />
        <div
          className={`media-wrapper ${layout === 'chat' ? 'media-wrapper--minimized-video' : ''}`}
        >
          <VideoWrapper
            layout={layout === 'chat' ? 'chat' : 'default'}
            onVideoClick={handleVideoClick}
            isVideoLoaded={isVideoLoaded}
          >
            <VideoPlayer
              isLoading={!isVideoLoaded}
              isCreatorMode={isCreatorMode}
              options={videoJsOptions}
              onReady={onPlayerReady}
              cuepoints={cuepoints}
              highlights={highlights}
              sections={sections}
              callToActions={callToActions}
              publication={publication}
              locale={locale}
            />
          </VideoWrapper>

          {hasChat(publication) && (
            <div
              className="chat-wrapper"
              style={
                layout === 'chat'
                  ? undefined
                  : {
                      position: 'absolute',
                      left: '-9999px',
                      pointerEvents: 'none',
                    }
              }
            >
              <ChatBot
                publication={publication}
                open={openChat || layout === 'chat'}
                layout={layout}
                mainDocumentId={mainDocument?.id}
                publicationDocuments={publication?.documents}
                firstQuery={firstQuery}
                nextQuery={nextQuery}
                documentIds={documentIds}
                organizationId={organizationId ?? ''}
                showSources={handleDisplaySource}
                onBeforeQuery={cleanHighlightAndCuepoints}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                previewAccessToken={getUrlAccessToken(searchParams)}
                isLegacy
              />
            </div>
          )}
        </div>
        <PoweredBy
          layout={layout}
          style={isVideoOnly ? { left: 'auto' } : {}}
        />
      </div>
    );
  },
);

interface LegacyRightPanelProps {
  showTitle: boolean;
  showLogo: boolean;
  publication: MediaProjectInstanceResponse;
  layout: Layout;
  setLayout: (layout: Layout) => void;
  mainDocument: import('../types').ProjectDocument | undefined;
  highlights: import('react-pdf-highlighter').IHighlight[];
  cuepoints: import('../types').Cuepoint[];
  pdfViewerRef: React.MutableRefObject<never>;
  changeLanguage: (lang: string) => void;
  isCreatorMode?: boolean;
  creatorDocumentFooter?: React.ReactNode;
}

const LegacyRightPanel: React.FC<LegacyRightPanelProps> = React.memo(
  function LegacyRightPanel(props: LegacyRightPanelProps) {
    const {
      showTitle,
      showLogo,
      publication,
      layout,
      setLayout,
      mainDocument,
      highlights,
      cuepoints,
      pdfViewerRef,
      changeLanguage,
      isCreatorMode,
      creatorDocumentFooter,
    } = props;

    const { trackEvent } = useAnalytics();
    const showLanguageSelector = shouldShowLanguageSelector(
      publication?.project_id,
    );

    const handleLayoutChange = useCallback(() => {
      const newLayout = layout === 'chat' ? 'video' : 'chat';
      setLayout(newLayout);
      const sessionId = getSessionId(
        `${publication?.project_id}:${publication?.instance_id}`,
      );
      if (newLayout === 'video') {
        trackEvent(ChatCloseEvent, { chat_session_id: sessionId });
      } else {
        trackEvent(ChatOpenEvent, { chat_session_id: sessionId });
      }
    }, [
      setLayout,
      trackEvent,
      layout,
      publication?.project_id,
      publication?.instance_id,
    ]);

    const listOfDocs = Object.values(publication?.documents ?? {});
    const documentsTab = listOfDocs.map(
      (doc: ProjectDocument, index: number) => ({
        key: doc.id,
        label: doc.name,
        children: (
          <TabWrapper
            highlights={highlights}
            document={doc}
            index={index}
            pdfViewerRef={pdfViewerRef}
            cuepoints={cuepoints}
          />
        ),
      }),
    );

    const { theme } = useAntTheme();
    const { t } = useContext(AppContext);
    const showDocumentAnimation = hasDocumentAnimation(publication);

    return (
      <div className="right-panel">
        <Flex>
          {showTitle && (
            <div className="page-title">
              <Typography.Title level={1} style={{ margin: 0, fontSize: 26 }}>
                {publication.title}
              </Typography.Title>
            </div>
          )}
        </Flex>
        <div className="right-panel-content">
          <Flex className="right-panel-content__toolbar">
            {layout === 'video' && hasChat(publication) ? (
              <Button
                shape="round"
                size="large"
                type="primary"
                id="btn-ask-questions"
                icon={<AskQuestionsIcon />}
                onClick={handleLayoutChange}
                style={{
                  minWidth: 200,
                  background:
                    'linear-gradient(58deg, rgba(26,66,216,1) 0%, rgba(244,0,206,1) 100%)',
                }}
              >
                {t('askQuestions')}
              </Button>
            ) : (
              <Flex gap="middle" align="start">
                {hasChat(publication) ? (
                  <Button
                    shape="round"
                    size="large"
                    onClick={handleLayoutChange}
                  >
                    <span style={{ fontSize: 16 }}>{t('closeChat')}</span>
                  </Button>
                ) : (
                  <span></span>
                )}
              </Flex>
            )}
            {showLogo && showLanguageSelector && (
              <div style={{ marginRight: 20 }}>
                <LanguageSelector changeLanguage={changeLanguage} />
              </div>
            )}
          </Flex>
          <Flex
            style={{
              width: '100%',
              height: hasChat(publication) ? 'calc(100% - 150px)' : '100%',
              background: '#fff',
              border: `1px solid ${theme.colorBorderSecondary}`,
              overflow: 'hidden',
            }}
          >
            {showDocumentAnimation &&
            documentsTab.length > 1 &&
            MULTIPLE_DOCS_DISPLAY ? (
              <StyledTabs defaultActiveKey="1" items={documentsTab} />
            ) : showDocumentAnimation && mainDocument ? (
              <PdfViewer
                url={mainDocument.path}
                ref={pdfViewerRef}
                highlights={highlights}
                cuepoints={cuepoints}
                isPrimary={true}
                documentPagesCount={mainDocument.nb_of_pages}
                isCreatorMode={isCreatorMode}
              />
            ) : null}
          </Flex>
          {creatorDocumentFooter}
        </div>
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────
// Shared styled components & animations
// ─────────────────────────────────────────────────────────────

const iconButtonAnimation = keyframes`
  0% { opacity: 1; }
  10% { opacity: 0; }
  20% { opacity: 1; }
  30% { opacity: 0; }
  40% { opacity: 1; }
`;

const svgColorAnimation = keyframes`
  0% {}
  20% { fill: white; }
  30% {}
  100% {}
`;

const MobilePlayWrapper = styled.div`
  .mobile-play-button {
    width: 100px;
    height: 100px;
    animation: ${iconButtonAnimation} 2s infinite;
    filter: drop-shadow(0 0 40px rgba(26, 66, 216, 0.6));
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
  }

  .mobile-pause-button {
    color: #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .mobile-pause-button svg {
    width: 100px;
    height: auto;
  }

  .mobile-play-button svg path {
    animation: ${svgColorAnimation} 1s infinite;
  }
`;

const PauseOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  transition:
    visibility 0.5s ease-in-out,
    opacity 0.3s ease-in-out;
  z-index: 3;
`;

// ─────────────────────────────────────────────────────────────
// App — main entry point
// ─────────────────────────────────────────────────────────────

export function App(props: ResponsiveAppProps) {
  if (props.isDocHeyGenius) {
    return (
      <React.Suspense fallback={null}>
        <LazyInDocApp {...props} />
      </React.Suspense>
    );
  }
  return <StandardApp {...props} />;
}

function StandardApp(props: ResponsiveAppProps) {
  const {
    creatorToolbar,
    creatorToolbarMobile,
    creatorDocumentFooter,
    creatorFooter,
    onShareClick,
    videoOverride,
    headerSlot,
    footerSlot,
  } = props;
  const isLegacy = !props.newMode;

  const {
    publication,
    projectId,
    publicationId,
    mediaUrl,
    posterUrl,
    mainDocument,
    organizationId,
    documentIds,
    showDocumentAnimation,
    layout,
    setLayout,
    firstQuery,
    setFirstQuery,
    nextQuery,
    setNextQuery,
    locale,
    changeLocale,
    suggestions,
    setSuggestions,
    openChat,
    setOpenChat,
    chatOpen,
    setChatOpen,
    chatHeight,
    setChatHeight,
    showMobilePlayButton,
    setShowMobilePlayButton,
    showPauseOverlay,
    setShowPauseOverlay,
    isVideoLoaded,
    setIsVideoLoaded,
    rerenderCount,
    setRerenderCount,
    showTitle,
    showLogo,
    showSideCtas,
    pipVideoSize,
    theme,
    cuepoints,
    highlights,
    sections,
    callToActions,
    cleanHighlightAndCuepoints,
    handleDisplaySource,
    playerRef,
    pdfViewerRef,
    isMobile,
    windowWidth,
    t,
    appContextValue,
  } = useResponsiveAppState(props);

  const mediaMetadata = publication?.media?.metadata;
  const projectAspectRatio =
    typeof mediaMetadata?.width === 'number' &&
    typeof mediaMetadata?.height === 'number'
      ? mediaMetadata.width / mediaMetadata.height
      : null;
  const baseLeftPanelSize = 50;

  // Mobile-format projects (portrait, aspect ratio < 1): size the left
  // panel so it fits `containerHeight * aspectRatio` of the PanelGroup's
  // width, letting the player use the full available height. Falls back to
  // the base size until the container has been measured or for
  // landscape/unknown aspect ratios. The PanelGroup is keyed by this value
  // so changing it forces a remount and the new `defaultSize` is freshly
  // applied (imperative `resize()` is a no-op when `autoSaveId` restores
  // from localStorage).
  const leftPanelDefaultSize = useMemo(() => {
    if (
      typeof window === 'undefined' ||
      projectAspectRatio === null ||
      projectAspectRatio >= 1
    ) {
      return baseLeftPanelSize;
    }
    const desiredWidth = window.innerHeight * projectAspectRatio;
    return (
      (desiredWidth / (window.innerWidth - (creatorToolbar ? 70 : 0))) * 100
    );
  }, [projectAspectRatio, baseLeftPanelSize, creatorToolbar]);

  const leftPanelMaxSize =
    projectAspectRatio && projectAspectRatio >= 1 ? 60 : leftPanelDefaultSize;

  const { memoizedVideoJsOptions, handlePlayerReady } = useVideoPlayerSetup({
    mediaUrl,
    posterUrl,
    isMobile,
    windowWidth,
    showDocumentAnimation,
    theme,
    playerRef,
    pdfViewerRef,
    setIsVideoLoaded,
    setShowMobilePlayButton,
    setShowPauseOverlay,
    layout: isLegacy ? layout : undefined,
    isPreview: props.isPreview,
    publicationId,
    projectId,
    organizationId,
    publicationTitle: publication?.title ?? '',
  });

  const { handleMobilePlayTap } = useMobilePlayControls({
    playerRef,
    showMobilePlayButton,
    setShowMobilePlayButton,
    showPauseOverlay,
    setShowPauseOverlay,
  });

  const cardRef = useRef<HTMLDivElement>(null);
  const { onDragStart } = useChatDragResize(cardRef, chatHeight, setChatHeight);

  const { chatSubmitButtonLabel } = useChatSubmitLabel({ theme, t });

  const { trackEvent } = useAnalytics();

  // Analytics: track chat open/close
  // Skip initial render — chatOpen starts as false, which would fire a
  // spurious ChatCloseEvent before the user ever interacts with the chat.
  const chatOpenMounted = useRef(false);
  useEffect(() => {
    if (isLegacy) return; // Legacy tracks via handleLayoutChange
    if (!chatOpenMounted.current) {
      chatOpenMounted.current = true;
      return;
    }
    const sessionId = getSessionId(`${projectId}:${publicationId}`);
    if (chatOpen) {
      // The chat overlay covers the video — pause playback so the learner
      // doesn't miss content while reading the chat.
      try {
        playerRef.current?.pause();
      } catch {
        // Player not ready or mid-teardown; nothing to pause.
      }
      trackEvent(ChatOpenEvent, { chat_session_id: sessionId });
    } else {
      trackEvent(ChatCloseEvent, { chat_session_id: sessionId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpen, isLegacy]);

  const onReset = useCallback(
    () => setRerenderCount((key) => ++key),
    [setRerenderCount],
  );

  const isChat = layout === 'chat';

  // ── Mobile section ──
  const mobileSection = isLegacy ? (
    <Mobile key={rerenderCount + 2}>
      <MobileOverlay
        callToActions={callToActions}
        documentIds={documentIds}
        organizationId={organizationId as string}
        publication={publication as MediaProjectInstanceResponse}
        playerRef={playerRef}
        setShowMobilePlayButton={setShowMobilePlayButton}
        openChat={openChat}
        changeLanguage={changeLocale}
        locale={locale}
        hasChat={hasChat(publication)}
      />
      <VideoPlayer
        options={memoizedVideoJsOptions}
        onReady={handlePlayerReady}
        cuepoints={cuepoints}
        highlights={highlights}
        sections={sections}
        callToActions={callToActions}
        documentIds={documentIds}
        organizationId={organizationId}
        publication={publication}
        onVideoClick={handleMobilePlayTap}
        pipVideoSize={pipVideoSize}
      />
      <div
        className="mobile-play-button-wrapper"
        role="button"
        tabIndex={0}
        onClick={handleMobilePlayTap}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleMobilePlayTap();
        }}
      >
        <MobilePlayWrapper>
          <div
            className="mobile-play-button"
            style={{
              visibility: showMobilePlayButton ? 'visible' : 'hidden',
              zIndex: 5,
            }}
          />
        </MobilePlayWrapper>
        <PauseOverlay
          style={{
            opacity: showPauseOverlay ? 1 : 0,
            visibility: showPauseOverlay ? 'visible' : 'hidden',
            zIndex: 5,
          }}
        >
          <MobilePlayWrapper>
            <div className="mobile-pause-button">
              <PauseOutlined />
            </div>
          </MobilePlayWrapper>
        </PauseOverlay>
        {showDocumentAnimation && mainDocument && (
          <PdfViewer
            url={mainDocument.path}
            ref={pdfViewerRef}
            highlights={highlights}
            cuepoints={cuepoints}
            isPrimary={true}
            documentPagesCount={mainDocument?.nb_of_pages}
          />
        )}
      </div>
    </Mobile>
  ) : (
    <Mobile key={rerenderCount + 2}>
      <MobileScreenSwap
        videoJsOptions={memoizedVideoJsOptions}
        onPlayerReady={handlePlayerReady}
        playerRef={playerRef}
        cuepoints={cuepoints}
        highlights={highlights}
        sections={sections}
        callToActions={callToActions}
        pipVideoSize={pipVideoSize}
        showDocumentAnimation={showDocumentAnimation}
        mainDocument={mainDocument}
        pdfViewerRef={pdfViewerRef}
        hasChat={hasChat(publication)}
        openChat={openChat}
        setOpenChat={setOpenChat}
        documentIds={documentIds}
        organizationId={organizationId as string}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        firstQuery={firstQuery}
        setFirstQuery={setFirstQuery}
        publication={publication as MediaProjectInstanceResponse}
        setShowMobilePlayButton={setShowMobilePlayButton}
        changeLanguage={changeLocale}
        locale={locale}
        isCreatorMode={Boolean(creatorToolbar)}
        creatorToolbar={creatorToolbarMobile ?? creatorToolbar}
        onShareClick={onShareClick}
        footerSlot={footerSlot}
      />
    </Mobile>
  );

  // ── Shared left panel props ──
  const sharedLeftPanelProps: LeftPanelProps = {
    showSideCtas,
    videoOnly: videoOnly(publication),
    publication: publication as MediaProjectInstanceResponse,
    mainDocument,
    videoJsOptions: memoizedVideoJsOptions,
    playerRef,
    cuepoints,
    highlights,
    sections,
    callToActions,
    organizationId,
    onPlayerReady: handlePlayerReady,
    showLogo,
    locale,
    isVideoLoaded,
    isCreatorMode: Boolean(creatorToolbar),
    videoOverride,
  };

  const isPromptProjectFlag = isPromptProject(publication);
  // Render the chat in its own right-panel space (always open, no overlay
  // trigger) when the experience is a prompt project, or when chat is
  // enabled but there's no document to display — there's nothing for the
  // chat to hide behind, so just show it.
  const chatInRightPanel =
    isPromptProjectFlag || (hasChat(publication) && !showDocumentAnimation);

  // ── Desktop section differs by chatPosition ──
  const desktopSection = isLegacy ? (
    <Desktop key={rerenderCount + 3}>
      <StyledApp $showLogo={showLogo}>
        <main className={isChat ? 'chat-mode' : ''}>
          {showDocumentAnimation || creatorToolbar ? (
            <PanelGroup
              key={`mediaPlayer-${leftPanelDefaultSize}`}
              direction="horizontal"
            >
              <Panel
                id="left"
                order={1}
                defaultSize={leftPanelDefaultSize}
                minSize={20}
                maxSize={leftPanelMaxSize}
              >
                <LegacyLeftPanel
                  {...sharedLeftPanelProps}
                  layout={layout}
                  setLayout={setLayout}
                  documentIds={documentIds}
                  handleDisplaySource={handleDisplaySource}
                  cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
                  firstQuery={firstQuery}
                  nextQuery={nextQuery}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  openChat={openChat}
                />
              </Panel>
              <PanelResizeHandle>
                <StyledPanelResizeHandle />
              </PanelResizeHandle>
              <Panel id="right" order={2} minSize={40}>
                {creatorToolbar}
                <LegacyRightPanel
                  showTitle={showTitle}
                  showLogo={showLogo}
                  publication={publication as MediaProjectInstanceResponse}
                  layout={layout}
                  setLayout={setLayout}
                  mainDocument={mainDocument}
                  highlights={highlights}
                  cuepoints={cuepoints}
                  pdfViewerRef={pdfViewerRef}
                  changeLanguage={changeLocale}
                  isCreatorMode={Boolean(creatorToolbar)}
                  creatorDocumentFooter={creatorDocumentFooter}
                />
                {creatorFooter}
              </Panel>
            </PanelGroup>
          ) : (
            <div className="centered-panel">
              <LegacyLeftPanel
                {...sharedLeftPanelProps}
                layout={layout}
                setLayout={setLayout}
                documentIds={documentIds}
                handleDisplaySource={handleDisplaySource}
                cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
                firstQuery={firstQuery}
                nextQuery={nextQuery}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                openChat={openChat}
              />
            </div>
          )}
          {/* Floating prompt form — legacy only */}
          {hasChat(publication) && (
            <div
              className="prompt-form-container"
              style={{
                position: 'absolute',
                bottom: showDocumentAnimation ? 20 : 50,
                width: showDocumentAnimation ? 'calc(60vw - 40px)' : 500,
                right: showDocumentAnimation ? 20 : 'auto',
                zIndex: 10000,
                transition: 'all 600ms, opacity 800ms',
                opacity: layout === 'chat' ? 0 : 1,
                pointerEvents: layout === 'chat' ? 'none' : 'all',
                transform:
                  layout === 'chat'
                    ? 'translateX(-750px) scale(0.6)'
                    : 'translateX(0px)',
              }}
            >
              <PromptForm
                inputPosition="floating"
                gradientBorder
                animatedSubmitButton={!chatSubmitButtonLabel}
                submitButtonLabel={chatSubmitButtonLabel}
                value=""
                height={60}
                fontSize={26}
                onChange={(value) => {
                  setLayout('chat');
                  setFirstQuery(value);
                }}
              />
              {layout === 'video' && (
                <SuggestionsOverlay
                  setNextQuery={setNextQuery}
                  playerRef={playerRef}
                  suggestions={suggestions}
                  setLayout={() => setLayout('chat')}
                />
              )}
            </div>
          )}
        </main>
      </StyledApp>
    </Desktop>
  ) : (
    <Desktop key={rerenderCount + 3}>
      <StyledApp $showLogo={showLogo}>
        <main>
          {showDocumentAnimation || creatorToolbar || chatInRightPanel ? (
            <PanelGroup
              key={`mediaPlayer-${leftPanelDefaultSize}`}
              direction="horizontal"
            >
              <Panel
                id="left"
                order={1}
                defaultSize={leftPanelDefaultSize}
                minSize={20}
                maxSize={
                  hasChat(publication) && chatOpen && !chatInRightPanel
                    ? 100
                    : leftPanelMaxSize
                }
              >
                <ModernLeftPanel
                  {...sharedLeftPanelProps}
                  chatOpen={chatOpen}
                  setChatOpen={setChatOpen}
                  documentIds={documentIds}
                  organizationId={organizationId}
                  handleDisplaySource={handleDisplaySource}
                  cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
                  firstQuery={firstQuery}
                  nextQuery={nextQuery}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  openChat={openChat}
                  isPromptProject={chatInRightPanel}
                />
              </Panel>
              <PanelResizeHandle>
                <StyledPanelResizeHandle />
              </PanelResizeHandle>
              <Panel id="right" order={2} minSize={40}>
                {creatorToolbar ?? (
                  <div className="logo-bar">
                    <Logo
                      showLogo
                      projectId={publication?.project_id}
                      customUrl={publication?.assets?.Logo?.path}
                      fallbackSrc="/images/Logo_Libertify_Horizontal_fond_blanc_RVB.png"
                      className=""
                      imgClassName="logo-bar__img"
                    />
                    {headerSlot}
                  </div>
                )}
                <ModernRightPanel
                  publication={publication as MediaProjectInstanceResponse}
                  hasChat={hasChat}
                  mainDocument={mainDocument}
                  hasDocumentAnimation={hasDocumentAnimation}
                  highlights={highlights}
                  cuepoints={cuepoints}
                  pdfViewerRef={pdfViewerRef}
                  organizationId={organizationId}
                  chatOpen={chatOpen}
                  setChatOpen={setChatOpen}
                  chatHeight={chatHeight}
                  onDragStart={onDragStart}
                  cardRef={cardRef}
                  firstQuery={firstQuery}
                  nextQuery={nextQuery}
                  openChat={openChat}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  documentIds={documentIds}
                  handleDisplaySource={handleDisplaySource}
                  cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
                  showTitle={showTitle}
                  showLogo={showLogo}
                  creatorDocumentFooter={creatorDocumentFooter}
                  isCreatorMode={Boolean(creatorToolbar)}
                  isPromptProject={chatInRightPanel}
                />
                {creatorFooter}
              </Panel>
            </PanelGroup>
          ) : (
            <div className="centered-panel">
              <ModernLeftPanel
                {...sharedLeftPanelProps}
                chatOpen={chatOpen}
                setChatOpen={setChatOpen}
                documentIds={documentIds}
                organizationId={organizationId}
                handleDisplaySource={handleDisplaySource}
                cleanHighlightAndCuepoints={cleanHighlightAndCuepoints}
                firstQuery={firstQuery}
                nextQuery={nextQuery}
                suggestions={suggestions}
                setSuggestions={setSuggestions}
                openChat={openChat}
                isPromptProject={chatInRightPanel}
              />
            </div>
          )}
        </main>
      </StyledApp>
    </Desktop>
  );

  return (
    <AppProviders
      theme={theme}
      appContextValue={appContextValue}
      onReset={onReset}
    >
      <div
        className={[
          'video-player-app',
          !isLegacy && 'fullscreen-video',
          !isLegacy && chatOpen && !chatInRightPanel && 'chat-open',
          creatorToolbar && 'creator-mode',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {mobileSection}
        {desktopSection}
        {/* MobileScreenSwap renders its own footerSlot */}
        <Desktop>{footerSlot}</Desktop>
        {isLegacy ? <Mobile>{footerSlot}</Mobile> : null}
      </div>
    </AppProviders>
  );
}

export default function ResponsiveApp(props: ResponsiveAppProps) {
  return <App {...props} />;
}
